import { useState, useEffect } from "react";
import { C, getFontFamily } from "../constants/colors";

/* ═══════════════════════════════════════════════════════════════
   SAMAJH — Exam Corner
   Two modes:
   🎯 Test Yourself — MCQ quiz from pre-generated questions
   📝 Exam Questions — Board-style questions by mark type
   ═══════════════════════════════════════════════════════════════ */

const SUBJ = {
  Science:   { icon: "🔬", grad: ["#065f46","#059669"], soft: "#E1F5EE", text: "#065f46", dot: "#059669", accent: C.primaryMid },
  Maths:     { icon: "📐", grad: ["#78350f","#b45309"], soft: "#FAEEDA", text: "#78350f", dot: "#d97706", accent: "#854F0B" },
  Geography: { icon: "🌍", grad: ["#14532d","#16a34a"], soft: "#EAF3DE", text: "#14532d", dot: "#16a34a", accent: "#3B6D11" },
  Economics: { icon: "💰", grad: ["#312e81","#6366f1"], soft: "#EEEDFE", text: "#312e81", dot: "#6366f1", accent: "#534AB7" },
};

function shuffle(a) { const b=[...a]; for(let i=b.length-1;i>0;i--){const j=Math.floor(Math.random()*(i+1));[b[i],b[j]]=[b[j],b[i]];} return b; }

// TEST-FLOW-PURPLE-APPLIED: Revise tab purple gradient for all test-flow headers and primary CTAs
const PURPLE_GRAD = "linear-gradient(135deg, #5B4BA0, #7C6BC4, #9B8DD8)";
const PURPLE_DARK = "#5B4BA0";

// CONSOLIDATED-FIX-5-APPLIED: SVG subject icons matching the Study tab style (replaces emoji)
function SubjectSvg({ name, color, size = 22 }) {
  const common = { fill: "none", stroke: color, strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", width: size, height: size };
  return (
    <svg viewBox="0 0 24 24" {...common}>
      {name === "Maths" && (<><path d="M4 4h16M4 12h16M4 20h8"/><circle cx="18" cy="18" r="3"/></>)}
      {name === "Science" && (<><path d="M9 3h6v5l4 9H5l4-9V3z"/><path d="M9 3h6"/></>)}
      {name === "Geography" && (<><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2c-3 3-3 9 0 18M12 2c3 3 3 9 0 18"/></>)}
      {name === "Economics" && (<><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 3v18"/></>)}
      {name === "History" && (<><circle cx="12" cy="12" r="9"/><path d="M12 6v6l4 2"/></>)}
      {name === "Civics" && (<><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 3v18"/></>)}
    </svg>
  );
}

// TEST-FLOW-FIX-2B-APPLIED: proper title-case — lowercases minor words, handles possessive "'s"
const MINOR_WORDS = new Set(["to","of","and","in","the","a","an","for","on","or","at","by","with","from","into","is","as"]);
function titleCaseChapter(slug) {
  const words = slug.replace(/-/g, " ").trim().split(/\s+/);
  return words.map((w, i) => {
    const lower = w.toLowerCase();
    // Standalone "s" after a word → possessive "'s" (e.g. "euclid-s-geometry" → "Euclid's Geometry")
    if (lower === "s" && i > 0) return "'s";
    // Minor words stay lowercase unless first
    if (i > 0 && MINOR_WORDS.has(lower)) return lower;
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }).join(" ").replace(/ 's/g, "'s"); // stitch the space before possessive
}

// REVISE-EXAM-STEP-1-APPLIED: landing screen removed. Entry is subjectPick (or chapterPick if initialSubject passed)
export function ExamCornerPage({ profile, lang, onBack, onChat, initialSubject }) {
  const ff = getFontFamily(lang);
  const isHi = lang === "hi";
  const currentClass = profile?.studentClass || 10;

  // Screens: subjectPick | chapterPick | quizPick | quiz | quizResult (landing deleted)
  const [screen, setScreen] = useState(initialSubject ? "chapterPick" : "subjectPick");
  const [loading, setLoading] = useState(false);

  // Data
  const [chaptersData, setChaptersData] = useState(null); // enriched chapter data by subject
  const [testBank, setTestBank] = useState(null); // test-questions.json

  // Selection — pre-populate from initialSubject if provided
  const [selSub, setSelSub] = useState(initialSubject || null);
  const [selCh, setSelCh] = useState(null);

  // Quiz state
  const [quizCount, setQuizCount] = useState(5);
  const [questions, setQuestions] = useState([]);
  const [qIdx, setQIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [scores, setScores] = useState([]);

  // ── Load data on mount ──
  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        // Load manifest
        const mRes = await fetch("/data/enriched/manifest.json");
        if (!mRes.ok) return;
        const manifest = await mRes.json();
        const files = manifest.files.filter(f => f.class === currentClass);

        // Group by subject → chapters
        const bySubject = {};
        files.forEach(f => {
          if (!bySubject[f.subject]) bySubject[f.subject] = [];
          bySubject[f.subject].push(f);
        });
        // Sort chapters numerically
        Object.keys(bySubject).forEach(s => {
          bySubject[s].sort((a, b) => a.chapter - b.chapter);
        });
        setChaptersData(bySubject);

        // Load test bank
        try {
          const tRes = await fetch("/data/enriched/test-questions.json");
          if (tRes.ok) setTestBank(await tRes.json());
        } catch (e) { console.log("No test bank"); }
      } catch (e) { console.error(e); }
      setLoading(false);
    }
    load();
  }, [currentClass]);

  // ── Start MCQ quiz ──
  const startQuiz = (sub, chapterKey, n) => {
    if (!testBank) return;
    // Find matching questions
    let matched = [];
    Object.entries(testBank).forEach(([key, chData]) => {
      if (chData.class !== currentClass || chData.subject !== sub) return;
      if (chData.chapter === chapterKey || key.includes(chapterKey.toLowerCase().replace(/[^a-z0-9]/g, '-'))) {
        matched.push(...(chData.questions || []));
      }
    });
    if (matched.length === 0) {
      // Try fuzzy match on chapter name
      Object.entries(testBank).forEach(([key, chData]) => {
        if (chData.class !== currentClass || chData.subject !== sub) return;
        if (chData.chapter.toLowerCase().includes(chapterKey.toLowerCase()) ||
            chapterKey.toLowerCase().includes(chData.chapter.toLowerCase())) {
          matched.push(...(chData.questions || []));
        }
      });
    }

    const shuffled = shuffle(matched).slice(0, n).map(q => ({
      question: q.question, options: q.options, correct: q.correct, explanation: q.explanation || "",
    }));

    if (shuffled.length > 0) {
      setQuestions(shuffled);
      setQIdx(0); setSelected(null); setAnswered(false); setScores([]);
      setScreen("quiz");
    }
  };

  const handleSelect = (idx) => {
    if (answered) return;
    setSelected(idx); setAnswered(true);
    const q = questions[qIdx];
    setScores(s => [...s, { correct: idx === q.correct, question: q.question, selectedIdx: idx, correctIdx: q.correct, explanation: q.explanation }]);
  };

  const handleNext = () => {
    if (qIdx < questions.length - 1) { setQIdx(i => i + 1); setSelected(null); setAnswered(false); }
    else setScreen("quizResult");
  };

  const handleSkip = () => {
    setScores(s => [...s, { correct: false, question: questions[qIdx].question, skipped: true }]);
    if (qIdx < questions.length - 1) { setQIdx(i => i + 1); setSelected(null); setAnswered(false); }
    else setScreen("quizResult");
  };

  const correctCount = scores.filter(s => s.correct).length;

  // ── Shared header ──
  const Header = ({ title, backTo, backFn }) => (
    <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 10 }}>
      <div onClick={() => backFn ? backFn() : backTo ? setScreen(backTo) : onBack()}
        style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 12, background: "white", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M10 12l-4-4 4-4" stroke={C.text} strokeWidth="2" strokeLinecap="round" /></svg>
        <span style={{ fontSize: 13, fontWeight: 600, color: C.text, fontFamily: ff }}>{isHi ? "वापस" : "Back"}</span>
      </div>
      {title && <span style={{ fontSize: 13, color: C.textMute, fontFamily: ff, fontWeight: 500 }}>/ {title}</span>}
    </div>
  );

  // ══════════════════════════════════════════
  //  LOADING
  // ══════════════════════════════════════════
  if (loading || !chaptersData) return (
    <div style={{ minHeight: "calc(100vh / 0.92)", zoom: 0.92, background: "#F5F3FA", fontFamily: ff, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ marginBottom: 12, animation: "pulse 1.5s ease-in-out infinite" }}><svg width="48" height="48" viewBox="0 0 48 48" fill="none"><rect x="8" y="12" width="24" height="20" rx="3" stroke="#534AB7" strokeWidth="2.5"/><path d="M12 8h16a3 3 0 013 3v20" stroke="#534AB7" strokeWidth="2.5" strokeLinecap="round"/><path d="M14 22l4 4 8-8" stroke="#534AB7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg></div>
        <p style={{ fontSize: 14, fontWeight: 500, color: C.textMute }}>{isHi ? "तैयार हो रहा है..." : "Loading..."}</p>
        <style>{`@keyframes pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.1)}}`}</style>
      </div>
    </div>
  );

  // REVISE-EXAM-STEP-1-APPLIED: Landing screen deleted. Flow now starts at subjectPick or chapterPick.

  // ══════════════════════════════════════════
  //  SUBJECT PICKER (shared for test & exam)
  // ══════════════════════════════════════════
  if (screen === "subjectPick") {
    const subs = Object.keys(chaptersData);
    return (
      <div style={{ minHeight: "calc(100vh / 0.92)", zoom: 0.92, background: "#F5F3FA", fontFamily: ff }}>
        <Header title={isHi ? "खुद को परखो" : "Test Yourself"} backFn={onBack} />
        <div style={{ padding: "0 20px 32px", maxWidth: 460, margin: "0 auto" }}>
          <div style={{ marginBottom: 20 }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: C.text, margin: "0 0 4px" }}>{isHi ? "विषय चुनें" : "Pick a subject"}</h2>
            <p style={{ fontSize: 12, color: C.textMute, margin: 0 }}>{isHi ? `कक्षा ${currentClass}` : `Class ${currentClass}`}</p>
          </div>

          {subs.map((sub, si) => {
            const sm = SUBJ[sub] || SUBJ.Science;
            const chapCount = chaptersData[sub].length;
            // TEST-FLOW-FIX-1A-APPLIED: thick top border removed. FIX-1D: tactile-card for tap feedback
            return (
              <div key={sub} className="tactile-card" onClick={() => { setSelSub(sub); setScreen("chapterPick"); }}
                style={{ background: "white", borderRadius: 18, marginBottom: 12, cursor: "pointer", overflow: "hidden", boxShadow: "0 2px 14px rgba(140,110,60,0.12)", border: "1px solid rgba(140,110,60,0.08)", animation: `slideUp 0.3s ${si * 0.06}s both` }}>
                <div style={{ padding: "16px 18px", display: "flex", alignItems: "center", gap: 14 }}>
                  {/* CONSOLIDATED-FIX-5-APPLIED: SVG subject icon replaces emoji */}
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: sm.soft, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <SubjectSvg name={sub} color={sm.dot} size={22} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{sub}</div>
                    <div style={{ fontSize: 11, color: C.textMute, marginTop: 2 }}>{chapCount} chapters</div>
                  </div>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke={sm.dot} strokeWidth="2" strokeLinecap="round" /></svg>
                </div>
              </div>
            );
          })}
          <style>{`@keyframes slideUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}`}</style>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════
  //  CHAPTER PICKER — Test mode
  // ══════════════════════════════════════════
  if (screen === "chapterPick") {
    const chapters = chaptersData[selSub] || [];
    const sm = SUBJ[selSub] || SUBJ.Science;
    return (
      <div style={{ minHeight: "calc(100vh / 0.92)", zoom: 0.92, background: "#F5F3FA", fontFamily: ff }}>
        <Header title={selSub} backTo="subjectPick" />
        <div style={{ padding: "0 20px 32px", maxWidth: 460, margin: "0 auto" }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: C.text, margin: "0 0 16px" }}>{isHi ? "अध्याय चुनें" : "Pick a chapter"}</h2>
          {chapters.map((ch, ci) => {
            // Count available questions
            const chKey = `class-${currentClass}-${selSub.toLowerCase()}-ch${ch.chapter}-${ch.slug}`;
            const qCount = testBank?.[chKey]?.totalQuestions || 0;
            // TEST-FLOW-FIX-2A + 2B + 2C APPLIED: dark amber text, proper title case, tactile-card
            return (
              <div key={ci} className={qCount > 0 ? "tactile-card" : undefined} onClick={() => { if (qCount > 0) { setSelCh(ch); setScreen("quizPick"); } }}
                style={{ background: "white", borderRadius: 16, padding: "16px 18px", marginBottom: 10, cursor: qCount > 0 ? "pointer" : "default", borderLeft: `4px solid ${qCount > 0 ? sm.dot : "#e2e8f0"}`, boxShadow: "0 1px 4px rgba(140,110,60,0.08)", opacity: qCount > 0 ? 1 : 0.5 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 11, background: sm.soft, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: sm.dot }}>{ch.chapter}</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{titleCaseChapter(ch.slug)}</div>
                    <div style={{ fontSize: 11, color: qCount > 0 ? "#B45309" : "#8B7355", fontWeight: 600, marginTop: 2 }}>
                      {qCount > 0 ? `${qCount} questions` : "No questions yet"}
                    </div>
                  </div>
                  {qCount > 0 && <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke={sm.dot} strokeWidth="2" strokeLinecap="round" /></svg>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════
  //  QUIZ COUNT PICK
  // ══════════════════════════════════════════
  if (screen === "quizPick") {
    const chName = titleCaseChapter(selCh.slug);
    const chKey = `class-${currentClass}-${selSub.toLowerCase()}-ch${selCh.chapter}-${selCh.slug}`;
    const available = testBank?.[chKey]?.totalQuestions || 0;

    return (
      <div style={{ minHeight: "calc(100vh / 0.92)", zoom: 0.92, background: "#F5F3FA", fontFamily: ff }}>
        {/* TEST-FLOW-PURPLE-APPLIED: quizPick header uses unified Revise purple */}
        <div style={{ background: PURPLE_GRAD, padding: "20px 20px 40px" }}>
          <div onClick={() => setScreen("chapterPick")} style={{ background: "rgba(255,255,255,0.2)", border: "none", cursor: "pointer", padding: "6px 12px", borderRadius: 20, color: "white", fontSize: 13, fontWeight: 600, marginBottom: 16, display: "inline-flex", alignItems: "center", gap: 6, fontFamily: ff }}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M8 2L4 6l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
            {isHi ? "वापस" : "Back"}
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.6)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>TEST · {selSub}</div>
          <div style={{ fontSize: 20, fontWeight: 600, color: "white", lineHeight: 1.2 }}>Ch {selCh.chapter}: {chName}</div>
        </div>
        <div style={{ padding: "28px 20px", maxWidth: 400, margin: "0 auto", marginTop: -16 }}>
          <div style={{ background: "white", borderRadius: 20, padding: "28px 24px", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: C.text, marginBottom: 6 }}>{isHi ? "कितने सवाल?" : "How many questions?"}</div>
            <div style={{ fontSize: 13, color: C.textMute, marginBottom: 24 }}>{available} {isHi ? "उपलब्ध" : "available"} • MCQ</div>
            {/* TEST-FLOW-FIX-3A-APPLIED: strong selected state (2px border + tint) vs unselected (faint border + grey) */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 24 }}>
              {[5, 10].map(n => {
                const selected = quizCount === n;
                // TEST-FLOW-PURPLE-APPLIED: quiz count selector uses purple accent
                return (
                  <div key={n} className="tactile-card" onClick={() => setQuizCount(n)} style={{
                    padding: "16px 12px", borderRadius: 14, textAlign: "center",
                    border: selected ? `2px solid ${PURPLE_DARK}` : "1.5px solid rgba(140,110,60,0.15)",
                    background: selected ? "rgba(91,75,160,0.08)" : "#FFFFFF",
                    cursor: "pointer", fontFamily: ff,
                  }}>
                    <div style={{ fontSize: 28, fontWeight: 700, color: selected ? PURPLE_DARK : "#8B7355" }}>{n}</div>
                    <div style={{ fontSize: 12, fontWeight: 500, color: selected ? PURPLE_DARK : "#8B7355", marginTop: 2 }}>{isHi ? "सवाल" : "questions"}</div>
                  </div>
                );
              })}
            </div>
            {/* TEST-FLOW-FIX-3B-APPLIED: arrow removed. FIX-3D: tactile-btn-primary. PURPLE: unified Revise color */}
            <div className="tactile-btn-primary" onClick={() => startQuiz(selSub, selCh.slug.replace(/-/g, ' '), Math.min(quizCount, available))}
              style={{ width: "100%", padding: "14px 0", background: PURPLE_GRAD, color: "white", borderRadius: 14, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: ff, textAlign: "center", boxShadow: "0 4px 16px rgba(91,75,160,0.25)", borderBottom: `3px solid ${PURPLE_DARK}` }}>
              {isHi ? "शुरू करें" : "Start test"}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════
  //  MCQ QUIZ
  // ══════════════════════════════════════════
  if (screen === "quiz" && questions.length > 0) {
    const q = questions[qIdx];
    return (
      <div style={{ minHeight: "calc(100vh / 0.92)", zoom: 0.92, background: "#F5F3FA", fontFamily: ff }}>
        {/* TEST-FLOW-PURPLE-APPLIED: quiz header uses unified Revise purple */}
        <div style={{ background: PURPLE_GRAD, padding: "16px 20px 28px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <div onClick={() => setScreen("quizPick")} style={{ background: "rgba(255,255,255,0.2)", border: "none", cursor: "pointer", padding: "5px 10px", borderRadius: 16, color: "white", fontSize: 13, fontWeight: 600, fontFamily: ff }}>✕</div>
            <span style={{ fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.8)" }}>{qIdx + 1} of {questions.length}</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.8)" }}>{scores.filter(s => s.correct).length} correct</span>
          </div>
          <div style={{ height: 4, background: "rgba(255,255,255,0.2)", borderRadius: 4, overflow: "hidden" }}>
            <div style={{ height: "100%", background: "white", borderRadius: 4, width: `${(qIdx / questions.length) * 100}%`, transition: "width .4s ease" }} />
          </div>
        </div>

        <div style={{ padding: "20px", maxWidth: 520, margin: "0 auto", marginTop: -8 }}>
          {/* TEST-FLOW-FIX-4D-APPLIED: question card staggered entrance, re-keyed on qIdx so animation replays per question */}
          <div key={`q-${qIdx}`} className="animate-in stagger-0" style={{ background: "white", borderRadius: 20, padding: "24px", marginBottom: 16, boxShadow: "0 2px 12px rgba(140,110,60,0.08)" }}>
            {/* TEST-FLOW-PURPLE-APPLIED */}
            <div style={{ fontSize: 11, fontWeight: 700, color: PURPLE_DARK, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>{isHi ? "सवाल" : "QUESTION"} {qIdx + 1}</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: C.text, lineHeight: 1.6 }}>{q.question}</div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
            {q.options.map((opt, i) => {
              let bg = "white", border = C.border, color = C.text;
              // TEST-FLOW-FIX-5A-APPLIED: dim unchosen options after reveal
              let dim = false;
              if (answered) {
                if (i === q.correct) { bg = "#f0fdf4"; border = "#16a34a"; color = "#15803d"; }
                else if (i === selected && i !== q.correct) { bg = "#fef2f2"; border = "#ef4444"; color = "#dc2626"; }
                else { dim = true; }
              // TEST-FLOW-PURPLE-APPLIED: selected highlight + letter badge use unified Revise purple
              } else if (selected === i) { bg = "rgba(91,75,160,0.08)"; border = PURPLE_DARK; }
              const badgeBg = answered && i === q.correct ? "#16a34a"
                : answered && i === selected && i !== q.correct ? "#ef4444"
                : "rgba(91,75,160,0.12)";
              const badgeColor = answered && (i === q.correct || (i === selected && i !== q.correct)) ? "white" : PURPLE_DARK;
              return (
                <div key={`opt-${qIdx}-${i}`} className={answered ? undefined : "tactile-card animate-in " + (i < 2 ? "stagger-1" : "stagger-2")} onClick={() => handleSelect(i)} style={{
                  background: bg, border: `1.5px solid ${border}`, borderRadius: 14,
                  padding: "14px 18px", textAlign: "left", cursor: answered ? "default" : "pointer",
                  fontFamily: ff, fontSize: 14, fontWeight: 600, color,
                  display: "flex", alignItems: "center", gap: 12,
                  opacity: dim ? 0.4 : 1,
                  pointerEvents: dim ? "none" : "auto",
                  transition: "opacity .2s ease",
                }}>
                  <span style={{ width: 32, height: 32, borderRadius: "50%", background: badgeBg, color: badgeColor, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
                    {answered && i === q.correct ? "✓" : answered && i === selected && i !== q.correct ? "✗" : ["A", "B", "C", "D"][i]}
                  </span>
                  {opt}
                </div>
              );
            })}
          </div>

          {!answered && <button onClick={handleSkip} style={{ width: "100%", padding: "12px", background: "none", border: "none", fontSize: 13, color: "#8B7355", cursor: "pointer", fontFamily: ff }}>{isHi ? "बाद में करूँगा" : "Skip for now"}</button>}

          {answered && (
            <div>
              {/* TEST-FLOW-FIX-5C-APPLIED: explanation shown for BOTH correct and wrong — green border if right, red if wrong */}
              <div style={{ background: selected === q.correct ? "rgba(26,122,82,0.04)" : "rgba(220,38,38,0.04)", borderLeft: `3px solid ${selected === q.correct ? "#1A7A52" : "#DC2626"}`, borderRadius: "0 10px 10px 0", padding: "12px 16px", marginBottom: 14, fontSize: 13, color: "#615030", lineHeight: 1.6, fontFamily: ff }}>
                <strong style={{ color: selected === q.correct ? "#1A7A52" : "#DC2626", marginRight: 4 }}>
                  {selected === q.correct ? (isHi ? "सही!" : "Correct!") : (isHi ? "गलत जवाब।" : "Incorrect.")}
                </strong>
                {q.explanation}
              </div>
              {/* TEST-FLOW-FIX-5B + 5D APPLIED: Next uses green #1A7A52, no arrow */}
              <div className="tactile-btn-primary" onClick={handleNext} style={{ width: "100%", padding: "14px 0", background: "#1A7A52", color: "white", borderRadius: 14, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: ff, textAlign: "center", borderBottom: "3px solid #15633F" }}>
                {qIdx < questions.length - 1 ? (isHi ? "अगला सवाल" : "Next") : (isHi ? "नतीजे देखें" : "See results")}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════
  //  QUIZ RESULTS
  // ══════════════════════════════════════════
  if (screen === "quizResult") {
    const skippedCount = scores.filter(s => s.skipped).length;
    const answeredCount = questions.length - skippedCount;
    const pct = answeredCount > 0 ? Math.round((correctCount / answeredCount) * 100) : 0;

    return (
      <div style={{ minHeight: "calc(100vh / 0.92)", zoom: 0.92, background: "#F5F3FA", fontFamily: ff }}>
        {/* TEST-FLOW-PURPLE-APPLIED: quizResult header uses unified Revise purple */}
        <div style={{ background: PURPLE_GRAD, padding: "20px 20px 40px", textAlign: "center" }}>
          <div style={{ fontSize: 52, marginBottom: 8 }}>{pct >= 80 ? "🏆" : pct >= 60 ? "✓" : "📚"}</div>
          <div style={{ fontSize: 26, fontWeight: 600, color: "white", marginBottom: 4 }}>{correctCount}/{answeredCount}</div>
          <div style={{ fontSize: 14, color: "rgba(255,255,255,0.7)" }}>{pct}% correct{skippedCount > 0 ? ` · ${skippedCount} skipped` : ""}</div>
        </div>
        <div style={{ padding: "20px", maxWidth: 480, margin: "0 auto", marginTop: -16 }}>
          <div style={{ background: "white", borderRadius: 20, padding: "24px", marginBottom: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize: 14, color: C.textMute, marginBottom: 16 }}>
              {pct >= 80 ? (isHi ? "शानदार!" : "Excellent!") : pct >= 60 ? (isHi ? "अच्छा! थोड़ी और प्रैक्टिस करो।" : "Good! A bit more practice will help.") : (isHi ? "इस टॉपिक को फिर से पढ़ो।" : "Review this topic, then try again.")}
            </div>
            <div style={{ height: 10, background: "#F5F3FA", borderRadius: 10, overflow: "hidden", marginBottom: 8 }}>
              {/* TEST-FLOW-PURPLE-APPLIED: mid-range uses Revise purple */}
              <div style={{ height: "100%", background: pct >= 80 ? "#16a34a" : pct >= 60 ? PURPLE_DARK : "#f59e0b", borderRadius: 10, width: `${pct}%`, transition: "width 1s ease" }} />
            </div>
          </div>

          {/* Breakdown */}
          <div style={{ background: "white", borderRadius: 20, padding: "20px", marginBottom: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.textMute, letterSpacing: "0.03em", marginBottom: 14 }}>BREAKDOWN</div>
            {scores.map((s, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 0", borderBottom: i < scores.length - 1 ? `1px solid ${C.border}` : "none" }}>
                <span style={{ width: 24, height: 24, borderRadius: 6, background: s.skipped ? "#f1f5f9" : s.correct ? "#f0fdf4" : "#fef2f2", color: s.skipped ? "#94a3b8" : s.correct ? "#16a34a" : "#ef4444", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{s.skipped ? "–" : s.correct ? "✓" : "✗"}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: s.skipped ? "#94a3b8" : C.text, lineHeight: 1.4, marginBottom: 3 }}>{s.question}{s.skipped ? " (skipped)" : ""}</div>
                  {!s.correct && !s.skipped && <div style={{ fontSize: 12, color: "#16a34a" }}>✓ {questions[i]?.options[s.correctIdx]}</div>}
                </div>
              </div>
            ))}
          </div>

          {/* TEST-FLOW-PURPLE-APPLIED: Try again CTA uses unified Revise purple */}
          <button onClick={() => { setQIdx(0); setSelected(null); setAnswered(false); setScores([]); startQuiz(selSub, selCh.slug.replace(/-/g, ' '), quizCount); }}
            style={{ width: "100%", padding: "14px", background: PURPLE_GRAD, color: "white", border: "none", borderRadius: 14, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: ff, marginBottom: 10 }}>
            {isHi ? "फिर से" : "Try again"}
          </button>
          {/* REVISE-EXAM-STEP-1-APPLIED: back goes to Revise tab (landing deleted) */}
          <button onClick={() => onBack?.()} style={{ width: "100%", padding: "12px", background: "none", color: C.textMute, border: "none", fontSize: 14, cursor: "pointer", fontFamily: ff }}>
            {isHi ? "वापस" : "Back to Revise"}
          </button>
        </div>
      </div>
    );
  }

  return null;
}
